import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, ModalWrapper, ModalCloseButton } from '../common';
import AddressSearch from './AddressSearch';
import AirportSelector from './AirportSelector';
import GolfCourseSelector from './GolfCourseSelector';
import SkiResortSelector from './SkiResortSelector';
import HospitalSelector from './HospitalSelector';
import { getGolfCourses, getSkiResorts } from '../../api/api/destinations';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

// 도착지 검색 시 ADMIN DB(Golf/Ski) 결과를 카카오 결과보다 상위로 노출.
// ADMIN 에 등록한 골프장/스키장이 카카오 검색 인덱스에 없어 클릭 시 가격 매칭이
// 빈 채로 250k fallback 으로 떨어지던 케이스 보정.
const ADMIN_SEARCH_FETCHERS = {
  golf: (q) => getGolfCourses({ search: q }),
  ski: (q) => getSkiResorts({ search: q }),
};

/**
 * 출발지/도착지/경유지 수정 모달
 *
 * - 출발지/경유지: AddressSearch (Type 1 - 지역검색 인라인 드롭다운)
 * - 도착지: 서비스별 셀렉터 (Type 2 - SelectorOverlay 기반)
 *   - airport → AirportSelector
 *   - golf → GolfCourseSelector
 *   - ski → SkiResortSelector
 *   - hospital → HospitalSelector
 *   - pet/business → AddressSearch
 *
 * onConfirm(newAddress): 확인 시 새 주소 문자열 전달
 */

/* 도착지에 셀렉터를 사용하는 서비스 목록
   golf/ski는 이제 arrival = 최종 도착지(AddressSearch 사용) */
const selectorTypes = ['airport', 'hospital'];

export default function RouteEditModal({ type, field, direction, currentValue, onConfirm, onClose, departure, arrival }) {
  const { t } = useTranslation();
  // #171: 영문 등 비한국어 환경에서 현재 주소(한글)가 그대로 노출되던 문제 — 앱 표준 주소 번역 적용
  const tCurrentValue = useDeepLTranslated(currentValue);

  /* 서비스별 도착지 restriction */
  const arrivalRestrictions = {
    golf: { message: t('form.region_restriction_seoul_gyeonggi'), allowGyeonggi: true },
    ski: { message: t('form.region_restriction_seoul_gyeonggi'), allowGyeonggi: true },
    pet: { message: t('form.region_restriction_seoul_only') },
    business: { message: t('form.region_restriction_seoul_only') },
    // #200: 투어 도착지 — 서울/경기/인천/강원 허용(stopover 와 동일 권역). 미지정 시 무제한이 되므로 명시.
    tour: { message: t('form.region_restriction_seoul_gangwon'), allowGyeonggi: true, allowIncheon: true, allowGangwon: true },
  };

  /* 출발지는 모든 서비스 서울 한정 */
  const departureRestriction = { message: t('form.region_restriction_seoul_only') };

  /* 서비스별 경유지 기본 지역 제한 (출발지/도착지 정보 없을 때 fallback) */
  const stopoverRestrictions = {
    airport:  { message: t('form.region_restriction_seoul'), allowGyeonggi: true, allowIncheon: true },
    golf:     { message: t('form.region_restriction_seoul_gangwon'), allowGyeonggi: true, allowIncheon: true, allowGangwon: true },
    ski:      { message: t('form.region_restriction_seoul_gyeonggi'), allowGyeonggi: true },
    hospital: { message: t('form.region_restriction_seoul_only') },
    pet:      { message: t('form.region_restriction_seoul_only') },
    business: { message: t('form.region_restriction_seoul_only') },
    // #200: 투어 경유지 — 서울/경기/인천/강원 허용(투어 권역: 남이섬·가평·춘천·에버랜드·DMZ 등).
    //       기존엔 투어 페이지가 type="pet"(서울 only)을 넘겨 인근 경유지가 "문의 필요 지역"으로 차단됨.
    tour:     { message: t('form.region_restriction_seoul_gangwon'), allowGyeonggi: true, allowIncheon: true, allowGangwon: true },
  };

  /* 주소에서 지역명 추출 → 경유지 restriction 동적 생성 */
  function buildStopoverRestriction(dep, arr, fallback) {
    if (!dep && !arr) return fallback;
    const regions = new Set();
    [dep, arr].forEach((addr) => {
      if (!addr) return;
      if (/^서울|서울특별시/.test(addr)) regions.add('서울');
      if (/^경기|경기도/.test(addr)) regions.add('경기');
      if (/^인천|인천광역시/.test(addr)) regions.add('인천');
      if (/^강원|강원도|강원특별자치도/.test(addr)) regions.add('강원');
    });
    // #200: 출발/도착 지역으로 "좁히지" 않고 서비스 기본 허용(fallback)에 합집합으로 추가.
    //       기존엔 서울→경기 예약 시 인천 등 인근 경유지가 막혀 "문의 필요 지역" 발생.
    const restriction = { ...fallback };
    if (regions.has('경기')) restriction.allowGyeonggi = true;
    if (regions.has('인천')) restriction.allowIncheon = true;
    if (regions.has('강원')) restriction.allowGangwon = true;
    return restriction;
  }

  const selectorPlaceholders = {
    airport: t('common.airport_select_placeholder'),
    golf: t('common.golf_select_placeholder'),
    ski: t('common.ski_select_placeholder'),
    hospital: t('common.hospital_select_placeholder'),
  };

  const fieldLabel = field === 'departure'
    ? t('common.departure_label')
    : field === 'stopover'
      ? t('common.stopover')
      : t('common.destination_label');

  const title = field === 'departure'
    ? t('common.route_edit_departure')
    : field === 'stopover'
      ? t('common.route_edit_stopover')
      : t('common.route_edit_arrival');

  /* 도착지 + 셀렉터 서비스인지 판단
     airport from-airport: departure=공항(selector), arrival=주소(address) */
  const isFromAirport = type === 'airport' && direction === 'from-airport';
  const useSelector = selectorTypes.includes(type) && (
    isFromAirport ? field === 'departure' : field === 'arrival'
  );

  /* 셀렉터 선택 핸들러 */
  const handleSelectorChange = (value, second, third) => {
    onConfirm(third || second || value);
  };

  /* 주소 검색 선택 핸들러 — #122: 장소명 함께 전달 */
  const handleAddressSelect = (address, name) => {
    onConfirm(address, name);
  };

  return (
    <ModalWrapper maxWidth="modal" padding="" gap="">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <h2 className="text-lg font-bold text-mono-900">{title}</h2>
        <ModalCloseButton onClick={onClose} size="sm" />
      </div>

      <div className="px-5 pb-5 flex flex-col gap-4">
        {/* 현재 주소 */}
        <div className="flex flex-col gap-1">
          <span className="text-2sm font-medium text-mono-500">
            {t('common.current_field', { field: fieldLabel })}
          </span>
          <span className="text-sm font-bold text-mono-900">{tCurrentValue || currentValue}</span>
        </div>

        {/* 선택 UI */}
        {useSelector ? (
          /* 도착지: 서비스별 셀렉터 (Type 2) */
          <>
            {type === 'airport' && (
              <AirportSelector
                value=""
                onChange={handleSelectorChange}
                placeholder={selectorPlaceholders.airport}
              />
            )}
            {type === 'golf' && (
              <GolfCourseSelector
                value=""
                onChange={handleSelectorChange}
                placeholder={selectorPlaceholders.golf}
              />
            )}
            {type === 'ski' && (
              <SkiResortSelector
                value=""
                onChange={handleSelectorChange}
                placeholder={selectorPlaceholders.ski}
              />
            )}
            {type === 'hospital' && (
              <HospitalSelector
                value=""
                onChange={handleSelectorChange}
                placeholder={selectorPlaceholders.hospital}
              />
            )}
          </>
        ) : (
          /* 출발지/경유지/반려동물·비즈니스 도착지: AddressSearch (Type 1) */
          <AddressSearch
            value=""
            onChange={handleAddressSelect}
            placeholder={t('common.field_search', { field: fieldLabel })}
            mode="inline"
            restriction={
              field === 'departure'
                ? departureRestriction
                : field === 'stopover'
                  ? buildStopoverRestriction(departure, arrival, stopoverRestrictions[type] || departureRestriction)
                  : arrivalRestrictions[type]
            }
            fetchAdmin={field === 'arrival' ? ADMIN_SEARCH_FETCHERS[type] : undefined}
          />
        )}

        {/* 요금 변경 안내 */}
        <div className="flex items-start gap-1.5">
          <Icon name="info" size={15} className="text-orange-500 shrink-0 mt-0.5" />
          <p className="flex-1 min-w-0 text-2sm font-medium text-orange-500 break-keep break-words">
            {t('common.address_change_notice')}
          </p>
        </div>

        {/* 취소 버튼 */}
        <Button level="level3" fullWidth onClick={onClose}>{t('common.cancel')}</Button>
      </div>
    </ModalWrapper>
  );
}
